import { isAxiosError } from 'axios'

export type AnalyticsEnvironment = 'development' | 'production'

export type AuthMethod = 'google' | 'mailru' | 'password' | 'telegram' | 'vk'

export type FailureCode =
  | 'dependency_unavailable'
  | 'forbidden'
  | 'invalid_response'
  | 'network'
  | 'payment_declined'
  | 'quota'
  | 'server'
  | 'timeout'
  | 'unauthorized'
  | 'unknown'
  | 'validation'

export type FailureStage =
  | 'authorization'
  | 'calculation'
  | 'data_loading'
  | 'external_redirect'
  | 'order_creation'
  | 'rendering'
  | 'request'

export type FlowId =
  | 'authentication'
  | 'checkout'
  | 'export'
  | 'payment_return'
  | 'registration'
  | 'seller_connection'
  | 'tool_use'

export type HttpStatusGroup = '4xx' | '5xx' | 'none'

export type RegistrationEntryPoint = 'auth_form'

const INTERNAL_USER_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export const isInternalUserId = (value: string | undefined): value is string =>
  value !== undefined && INTERNAL_USER_ID_PATTERN.test(value)

export interface CheckoutStartedProperties {
  amountMinor: number
  currency: 'RUB'
  projectId: string
  tariffId: string
}

export interface FlowFailedProperties {
  attempt?: number
  failureCode: FailureCode
  failureStage: FailureStage
  flowId: FlowId
  httpStatusGroup: HttpStatusGroup
  isRetryable: boolean
}

export type FlowFailureContext = Pick<FlowFailedProperties, 'failureCode' | 'httpStatusGroup' | 'isRetryable'>

export const getFlowFailureCause = (error: unknown): unknown =>
  error instanceof Error && error.cause !== undefined ? error.cause : error

export const getFlowFailureContext = (error: unknown): FlowFailureContext => {
  const cause = getFlowFailureCause(error)

  if (!isAxiosError(cause)) {
    return { failureCode: 'unknown', httpStatusGroup: 'none', isRetryable: false }
  }

  if (cause.code === 'ECONNABORTED' || cause.code === 'ETIMEDOUT') {
    return { failureCode: 'timeout', httpStatusGroup: 'none', isRetryable: true }
  }

  const status = cause.response?.status
  if (status === undefined) {
    return { failureCode: 'network', httpStatusGroup: 'none', isRetryable: true }
  }
  if (status === 401) {
    return { failureCode: 'unauthorized', httpStatusGroup: '4xx', isRetryable: false }
  }
  if (status === 403) {
    return { failureCode: 'forbidden', httpStatusGroup: '4xx', isRetryable: false }
  }
  if (status === 429) {
    return { failureCode: 'quota', httpStatusGroup: '4xx', isRetryable: true }
  }
  if (status >= 400 && status < 500) {
    return { failureCode: 'validation', httpStatusGroup: '4xx', isRetryable: false }
  }
  if (status >= 500) {
    return { failureCode: 'server', httpStatusGroup: '5xx', isRetryable: true }
  }

  return { failureCode: 'unknown', httpStatusGroup: 'none', isRetryable: false }
}
